/* ═════════════════════════════════════════════════════════════════════════════
   SRC/COMPONENTS/TABDRIVERSVIEW/DATATRANSFORMS.JS — funções puras de dados
   ─────────────────────────────────────────────────────────────────────────────
   O QUE FAZ
   Transformações de dados (sem React, sem Redux, sem rede) usadas por
   DriversView (./index.jsx) para montar os gráficos da aba de Drivers.
   Extraídas do componente pra poderem ser testadas isoladamente — eram
   funções internas que dependiam só dos parâmetros recebidos, nunca de
   props/state do componente, então a extração não muda nenhum comportamento.

   Ver dataTransforms.test.js para a cobertura — inclui um teste de regressão
   específico pra buildHistEtapaData: o bug corrigido em "Corrige filtro de
   etapa no gráfico de evolução de sentimento" (chamar esta função com o
   dataset inteiro em vez do já filtrado pela etapa ativa, fazendo o gráfico
   nunca mudar ao trocar o filtro).

   USADO POR
   ./index.jsx (DriversView).
   ═══════════════════════════════════════════════════════════════════════════ */

const ORDEM_MESES = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

/**
 * Agrupa driverEssence por mês e calcula o score (pos-neg)/total para cada
 * pilar (Sucesso/Esforço/Emoção), ordenado cronologicamente.
 * @param {Array<{period_name: string, essence: string, positive: number|string, neutral: number|string, negative: number|string}>} driverEssence
 */
export function transformDataHistPillar(driverEssence) {
  const mesesMap = {};

  driverEssence.forEach((item) => {
    const [mes] = item.period_name.split("/");

    const pos = Number(item.positive) || 0;
    const neu = Number(item.neutral) || 0;
    const neg = Number(item.negative) || 0;

    const total = pos + neu + neg;

    // escolha do cálculo (ajuste se quiser)
    const score = total === 0 ? 0 : Math.round(((pos - neg) / total) * 100);

    if (!mesesMap[mes]) {
      mesesMap[mes] = {
        mes,
        Sucesso: 0,
        Esforço: 0,
        Emoção: 0,
      };
    }

    // normaliza nome da essência
    const key = item.essence.charAt(0).toUpperCase() + item.essence.slice(1);

    mesesMap[mes][key] = score;
  });

  // transforma em array ordenado
  return Object.values(mesesMap).sort((a, b) => {
    return ORDEM_MESES.indexOf(a.mes) - ORDEM_MESES.indexOf(b.mes);
  });
}

/** Nomes dos `limit` drivers com mais menções em `data`, maior volume primeiro. */
export function getTopDrivers(data, limit = 6) {
  const volumePorDriver = {};

  data.forEach((item) => {
    const driver = item.driver_name;
    const mentions = Number(item.total_mentions);

    volumePorDriver[driver] = (volumePorDriver[driver] || 0) + mentions;
  });

  return Object.entries(volumePorDriver)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([driver]) => driver);
}

/**
 * Top 6 drivers de `data` como linhas de gráfico (key/name/stroke) + o mapa
 * nome→key e a lista de nomes, pra uso por transformarHistData.
 */
export function transformarDriverLines(data) {
  const cores = [
    "#16a34a",
    "#dc2626",
    "#0284c7",
    "#7C3AED",
    "#d97706",
    "#0891b2",
    "#ec4899",
    "#14b8a6",
    "#f97316",
    "#6366f1",
  ];

  // pega top 6
  const topDrivers = getTopDrivers(data, 6);

  const lines = topDrivers.map((driver, index) => ({
    key: `dr_${index}`,
    name: driver,
    stroke: cores[index % cores.length],
  }));

  const driverMap = {};

  lines.forEach((item) => {
    driverMap[item.name] = item.key;
  });

  return {
    lines,
    driverMap,
    topDrivers,
  };
}

/**
 * Agrupa `data` por mês, só para os drivers em `topDrivers`, com o score
 * (total_positive - total_negative) de cada um na coluna driverMap[nome].
 */
export function transformarHistData(data, driverMap, topDrivers) {
  const agrupadoPorMes = {};

  data.forEach((item) => {
    // ignora drivers fora do top 6
    if (!topDrivers.includes(item.driver_name)) {
      return;
    }

    const mes = item.period_name.split("/")[0];

    if (!agrupadoPorMes[mes]) {
      agrupadoPorMes[mes] = {
        mes,
      };
    }

    const positive = Number(item.total_positive);
    const negative = Number(item.total_negative);

    const score = positive - negative;

    const key = driverMap[item.driver_name];

    agrupadoPorMes[mes][key] = score;
  });

  // Garante todas as keys: sem isso, um driver do top sem menção num mês
  // fica undefined naquele ponto e o Recharts para de desenhar a linha a
  // partir dali (em vez de descer a 0) — mesma proteção de buildHistEtapaData.
  const driverKeys = Object.values(driverMap);
  Object.values(agrupadoPorMes).forEach((row) => {
    driverKeys.forEach((key) => {
      if (row[key] === undefined || Number.isNaN(row[key])) {
        row[key] = 0;
      }
    });
  });

  return Object.values(agrupadoPorMes);
}

/**
 * Monta os dados do gráfico "Evolução do sentimento por etapa — variação em
 * pp": top `limit` drivers de `data` por volume, com o net sentiment
 * ((positivas-negativas)/menções) de cada mês, uma coluna por driver.
 *
 * IMPORTANTE: `data` deve já vir filtrado pela etapa ativa (ex.:
 * driverMetricsMonthly.filter(e => e.metric_name === etapaAtiva)) — passar o
 * dataset inteiro faz o top 6 e as linhas serem sempre os mesmos,
 * independente de qual etapa o usuário selecionou (ver teste de regressão).
 */
export function buildHistEtapaData(data, limit = 6) {
  const palette = ["#16a34a", "#dc2626", "#0284c7", "#7C3AED", "#d97706", "#0891b2"];

  // =========================
  // TOP DRIVERS POR VOLUME
  // =========================

  const volumeByDriver = {};

  data.forEach((item) => {
    const driver = item.driver_name;

    volumeByDriver[driver] = (volumeByDriver[driver] || 0) + Number(item.total_mentions || 0);
  });

  const topDrivers = Object.entries(volumeByDriver)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit);

  // =========================
  // DRIVER LINES
  // =========================

  const driverLines = topDrivers.map(([driver], index) => ({
    key: `etap_${index}`,
    name: driver,
    stroke: palette[index % palette.length],
  }));

  // =========================
  // DRIVER MAP
  // =========================

  const driverMap = {};

  driverLines.forEach((line) => {
    driverMap[line.name] = line.key;
  });

  // =========================
  // AGRUPA POR MÊS
  // =========================

  const groupedByMonth = {};

  data.forEach((item) => {
    const driver = item.driver_name;

    // ignora drivers fora do top
    if (!driverMap[driver]) {
      return;
    }

    const mes = item.period_name.split("/")[0];

    if (!groupedByMonth[mes]) {
      groupedByMonth[mes] = {
        mes,
      };
    }

    const mentions = Number(item.total_mentions || 0);

    const positive = Number(item.total_positive || 0);

    const negative = Number(item.total_negative || 0);

    // NET SENTIMENT
    const net = Math.round(((positive - negative) / Math.max(mentions, 1)) * 100);

    const key = driverMap[driver];

    groupedByMonth[mes][key] = net;
  });

  // =========================
  // GARANTE TODAS AS KEYS
  // =========================

  Object.values(groupedByMonth).forEach((row) => {
    driverLines.forEach((line) => {
      if (row[line.key] === undefined || Number.isNaN(row[line.key])) {
        row[line.key] = 0;
      }
    });
  });

  // =========================
  // ORDENA MESES
  // =========================

  const histData = Object.values(groupedByMonth).sort(
    (a, b) => ORDEM_MESES.indexOf(a.mes) - ORDEM_MESES.indexOf(b.mes),
  );

  return {
    driverLinesEt: driverLines,
    histEtapaData: histData,
    topDrivers,
  };
}
